
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { ReactNode } from 'react';
import { SxProps } from '@mui/material';
import { DefaultThemeOptions } from '../../../config/theme';
import { isReactNativeWebView } from '../../../utils/common';

interface CarouselContainerProps extends BoxProps {
  bgColor?: string;
}

interface ResponsiveBreakpointSettings {
  showArrow?: boolean;
  showDot?: boolean;
  showChip?: boolean;
  showIndex?: boolean;
  slidesPerView?: number;
  slideHeight?: number;
  gap?: number;
  showSeeAllButton?: boolean;
  showSeeAllCard?: boolean;
  fontStyle?: {
    fontTitleSize?: number;
    fontTitleWeight?: number;
    fontContentSize?: number;
    fontContentWeight?: number;
  };
}

export interface CarouselSettings extends ResponsiveBreakpointSettings {
  responsive?: {
    mobile?: ResponsiveBreakpointSettings;
    tablet?: ResponsiveBreakpointSettings;
    laptop?: ResponsiveBreakpointSettings;
    desktop?: ResponsiveBreakpointSettings;
    desktopLarge?: ResponsiveBreakpointSettings;
  };
}

interface Props {
  ref?: React.Ref<HTMLElement | null>;
  className?: string;
  loading?: boolean;
  topSection?: ReactNode;
  footer?: ReactNode;
  settings?: CarouselSettings;
  eventActionCallback?: (eventType: 'onNextClick' | 'onPrevClick' | 'onScreen' | 'onDotClick', e?: Event) => void;
  bgColor?: string;
  as?: React.ElementType;
}

export type UseCarouselProps = Props;

interface UseCarouselStyleReturn {
  settings?: CarouselSettings;
  getCarouseSlideRootStyle: () => {
    sx: SxProps;
    'data-testid': string;
  };
  getCarouseWrapperStyle: () => {
    sx: SxProps;
    'data-testid': string;
  };
  getCarouseViewportStyle: () => {
    sx: SxProps;
    'data-testid': string;
  };
  getCarouseContainerStyle: () => {
    sx: SxProps;
    'data-testid': string;
  };
  getCarouseSlideStyle: (
    isLastSlide?: boolean,
    showSeeAll?: boolean | 0,
    isNormalCard?: boolean,
    overWriteFlexBasis?: boolean,
    isHorizonProductCardEnabled?: boolean,
    isNative?: boolean,
  ) => {
    sx: SxProps;
    'data-testid': string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getResponsiveArrowVisibility: () => any;
  getCompactCarouselStyle: () => any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getNumericValue = (value: any): number | undefined => {
  return typeof value === 'number' ? value : undefined;
};

export const ProductCarouselContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<CarouselContainerProps>(({ bgColor, theme }) => ({
  position: 'relative',
  boxSizing: 'border-box',
  backgroundColor: bgColor || 'transparent',
}));

// Helper function to convert settings to custom breakpoint values
const getResponsiveValue = (
  settings: CarouselSettings | undefined,
  property: 'slidesPerView' | 'gap' | 'slideHeight' | 'showArrow',
) => {
  if (!settings) return undefined;

  const baseValue = settings[property];
  const responsive = settings.responsive;

  if (!responsive) return baseValue;

  return {
    xs: baseValue, // < 640px (default)
    sm: responsive.mobile?.[property] ?? baseValue, // 640px - 767px (mobile)
    md: responsive.tablet?.[property] ?? baseValue, // 768px - 1023px (tablet)
    lg: responsive.laptop?.[property] ?? baseValue, // 1024px - 1279px (laptop)
    xl: responsive.desktop?.[property] ?? baseValue, // 1280px - 1535px (desktop)
  };
};

const getSlidesPerViewResponsive = (settings: CarouselSettings | undefined) => {
  const responsiveValue = getResponsiveValue(settings, 'slidesPerView');

  if (typeof responsiveValue === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {
      xs: (() => {
        const val = getNumericValue(responsiveValue.xs);
        return val && val > 0 ? `${100 / val}%` : '100%';
      })(),
      sm: (() => {
        const val = getNumericValue(responsiveValue.sm);
        return val && val > 0 ? `${100 / val}%` : '100%';
      })(),
      md: (() => {
        const val = getNumericValue(responsiveValue.md);
        return val && val > 0 ? `${100 / val}%` : '100%';
      })(),
      lg: (() => {
        const val = getNumericValue(responsiveValue.lg);
        return val && val > 0 ? `${100 / val}%` : '100%';
      })(),
      xl: (() => {
        const val = getNumericValue(responsiveValue.xl);
        return val && val > 0 ? `${100 / val}%` : '100%';
      })(),
    };

    if (settings?.responsive?.desktopLarge?.slidesPerView) {
      const val = getNumericValue(settings.responsive.desktopLarge.slidesPerView);
      if (val && val > 0) {
        result['@media (min-width: 1536px)'] = `${100 / val}%`;
      }
    }

    return result;
  }

  const numericValue = getNumericValue(responsiveValue);
  return numericValue && numericValue > 0 ? `${100 / numericValue}%` : '100%';
};

const getGapResponsive = (settings: CarouselSettings | undefined) => {
  const responsiveValue = getResponsiveValue(settings, 'gap');

  if (typeof responsiveValue === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {
      xs: (() => {
        const val = getNumericValue(responsiveValue.xs);
        return val ? `${val * 8}px` : '6px';
      })(),
      sm: (() => {
        const val = getNumericValue(responsiveValue.sm);
        return val ? `${val * 8}px` : '6px';
      })(),
      md: (() => {
        const val = getNumericValue(responsiveValue.md);
        return val ? `${val * 8}px` : '6px';
      })(),
      lg: (() => {
        const val = getNumericValue(responsiveValue.lg);
        return val ? `${val * 8}px` : '6px';
      })(),
      xl: (() => {
        const val = getNumericValue(responsiveValue.xl);
        return val ? `${val * 8}px` : '6px';
      })(),
    };

    if (settings?.responsive?.desktopLarge?.gap !== undefined) {
      const val = getNumericValue(settings.responsive.desktopLarge.gap);
      result['@media (min-width: 1536px)'] = val ? `${val * 8}px` : '6px';
    }

    return result;
  }

  const numericValue = getNumericValue(responsiveValue);
  return numericValue ? `${numericValue * 8}px` : '6px';
};

const getHeightResponsive = (settings: CarouselSettings | undefined) => {
  const responsiveValue = getResponsiveValue(settings, 'slideHeight');

  if (typeof responsiveValue === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {
      xs: (() => {
        const val = getNumericValue(responsiveValue.xs);
        return val ? `${val * 8}px` : 'auto';
      })(),
      sm: (() => {
        const val = getNumericValue(responsiveValue.sm);
        return val ? `${val * 8}px` : 'auto';
      })(),
      md: (() => {
        const val = getNumericValue(responsiveValue.md);
        return val ? `${val * 8}px` : 'auto';
      })(),
      lg: (() => {
        const val = getNumericValue(responsiveValue.lg);
        return val ? `${val * 8}px` : 'auto';
      })(),
      xl: (() => {
        const val = getNumericValue(responsiveValue.xl);
        return val ? `${val * 8}px` : 'auto';
      })(),
    };

    if (settings?.responsive?.desktopLarge?.slideHeight !== undefined) {
      const val = getNumericValue(settings.responsive.desktopLarge.slideHeight);
      result['@media (min-width: 1536px)'] = val ? `${val * 8}px` : 'auto';
    }

    return result;
  }

  const numericValue = getNumericValue(responsiveValue);
  return numericValue ? `${numericValue * 8}px` : 'auto';
};

export function useCarouselStyle(originalProps: UseCarouselProps): UseCarouselStyleReturn {
  const { settings } = originalProps;

  const getCarouseSlideRootStyle = () => {
    return {
      sx: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      },
      'data-testid': 'carousel-slide-root',
    };
  };

  const getCompactCarouselStyle = () => {
    return {
      sx: {
        display: 'flex',
        flex: 0,
        height: '100%',
      },
    };
  };
  const getCarouseWrapperStyle = () => {
    return {
      sx: {
        position: 'relative',
        width: '100%',
        margin: '0 auto',
      },
      'data-testid': 'carousel-wrapper',
    };
  };

  const getCarouseViewportStyle = () => {
    return {
      sx: {
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '100%',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
      'data-testid': 'carousel-viewport',
    };
  };

  const getCarouseContainerStyle = () => {
    const gapValue = getGapResponsive(settings);

    return {
      sx: {
        display: 'flex',
        touchAction: 'pan-y pinch-zoom',
        backfaceVisibility: 'hidden',
        marginLeft:
          typeof gapValue === 'object'
            ? {
                xs: `-${gapValue.xs}`,
                sm: `-${gapValue.sm}`,
                md: `-${gapValue.md}`,
                lg: `-${gapValue.lg}`,
                xl: `-${gapValue.xl}`,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...((gapValue as any)['@media (min-width: 1536px)'] && {
                  '@media (min-width: 1536px)': `-${gapValue['@media (min-width: 1536px)']}`,
                }),
              }
            : `-${gapValue}`,
      },
      'data-testid': 'carousel-container',
    };
  };

  const getCarouseSlideStyle = (
    isLastSlide?: boolean,
    showSeeAll?: boolean | 0,
    isNormalCard?: boolean,
    overWriteFlexBasis?: boolean,
    isHorizonProductCardEnabled?: boolean,
    isNative?: boolean,
  ) => {
    const theme = DefaultThemeOptions;
    const flexBasisValue = getSlidesPerViewResponsive(settings);
    const paddingValue = getGapResponsive(settings);
    const heightValue = getHeightResponsive(settings);

    const isSeeAllCard = isLastSlide && showSeeAll;
    const seeAllMinWidth = isReactNativeWebView()
      ? '120px !important'
      : '80px !important';
    return {
      sx: {
        minWidth: isSeeAllCard ? seeAllMinWidth : isNormalCard ? '226.8px' : 0,
        width: isSeeAllCard ? '80px !important' : null,
        maxWidth: isSeeAllCard ? '120px !important' : null,
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
          minWidth: isHorizonProductCardEnabled && isNative ? '104px' : isNormalCard ? '180px' : 0,
        },
        flexGrow: 0,
        boxSizing: 'border-box',
        flexBasis:
          isHorizonProductCardEnabled && isNative
            ? '30%'
            : overWriteFlexBasis
              ? '35%'
              : isSeeAllCard
                ? '80px !important'
                : typeof flexBasisValue === 'object'
                  ? {
                      xs: flexBasisValue.xs,
                      sm: flexBasisValue.sm,
                      md: flexBasisValue.md,
                      lg: flexBasisValue.lg,
                      xl: flexBasisValue.xl,
                      ...((flexBasisValue as any)['@media (min-width: 1536px)'] && {
                        '@media (min-width: 1536px)': flexBasisValue['@media (min-width: 1536px)'] as string,
                      }),
                    }
                  : flexBasisValue,
        paddingLeft: isSeeAllCard
          ? '0 !important'
          : typeof paddingValue === 'object'
            ? {
                xs: paddingValue.xs,
                sm: paddingValue.sm,
                md: paddingValue.md,
                lg: paddingValue.lg,
                xl: paddingValue.xl,
                ...((paddingValue as any)['@media (min-width: 1536px)'] && {
                  '@media (min-width: 1536px)': paddingValue['@media (min-width: 1536px)'] as string,
                }),
              }
            : paddingValue,
        height:
          typeof heightValue === 'object'
            ? {
                xs: heightValue.xs,
                sm: heightValue.sm,
                md: heightValue.md,
                lg: heightValue.lg,
                xl: heightValue.xl,
                ...((heightValue as any)['@media (min-width: 1536px)'] && {
                  '@media (min-width: 1536px)': heightValue['@media (min-width: 1536px)'] as string,
                }),
              }
            : heightValue,
        marginLeft: isSeeAllCard ? '10px' : null,
        marginRight: isSeeAllCard ? '10px' : null,
      },
      'data-testid': 'carousel-slide',
    };
  };
  const getResponsiveArrowVisibility = () => {
    const showArrowValue = settings?.showArrow ?? false;

    return {
      display: showArrowValue ? 'block' : 'none',
    };
  };

  return {
    settings,
    getCarouseSlideRootStyle,
    getCarouseWrapperStyle,
    getCarouseViewportStyle,
    getCarouseContainerStyle,
    getCarouseSlideStyle,
    getResponsiveArrowVisibility,
    getCompactCarouselStyle,
  };
}
